import { useEffect, useId, useRef, useState } from 'react';

import { SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';

import { COLUMNS, type CandidateColumnKey } from '../columns';
import styles from './ColumnChooser.module.css';

/**
 * The popover that picks which of the twenty-two columns are shown.
 *
 * Not built on `useOverlay`, which is for modals: that hook locks page scroll and traps
 * Tab, and doing either to a popover hanging off a toolbar button would be worse than
 * the plain behaviour. Tab walking out of the list and closing it is correct here. The
 * two listeners below mirror the row menu on the staff list.
 */
export function ColumnChooser({
  visible,
  onToggle,
  onReset,
  isDefault,
}: {
  visible: readonly CandidateColumnKey[];
  onToggle: (key: CandidateColumnKey) => void;
  onReset: () => void;
  isDefault: boolean;
}) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /**
   * Where the panel sits, in viewport coordinates.
   *
   * It has to be `position: fixed`, which is why this exists. Absolutely positioned,
   * the panel is clipped by the `overflow: hidden` on the surrounding Panel, which is
   * invisible on a full page of rows and severe on a short one: with the empty state
   * showing, two thirds of the list was cut off and the reset button was unreachable.
   * Fixed positioning escapes the clip entirely, at the cost of having to measure.
   */
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    };

    place();

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus goes back where it came from, or it lands on the document body and the
      // next Tab starts from the top of the page (plan 9.5).
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    // Fixed to the viewport, so it has to follow the trigger when the page moves
    // underneath it. Capture, because the scroll may be inside the table wrapper
    // rather than on the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        icon={<SlidersHorizontal size={14} />}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        Columns ({visible.length})
      </Button>

      {open && anchor ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Show columns"
          className={styles.panel}
          style={{ insetBlockStart: anchor.top, insetInlineEnd: anchor.right }}
        >
          <p className={styles.head}>Show columns</p>

          <ul className={styles.list} role="list">
            {COLUMNS.map((column) => (
              <li key={column.key}>
                <Checkbox
                  label={column.label}
                  checked={visible.includes(column.key)}
                  // A real disabled input, so the name column cannot be unticked by
                  // keyboard either, not a dimmed div that swallows the click.
                  disabled={column.locked}
                  onChange={() => {
                    onToggle(column.key);
                  }}
                />
              </li>
            ))}
          </ul>

          {/*
            Not in the prototype, and required here: once the choice survives a
            reload, somebody who hides twenty columns is stuck with that table on
            every visit with no way back.
          */}
          <div className={styles.footer}>
            <Button variant="ghost" size="sm" disabled={isDefault} onClick={onReset}>
              Reset to default
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
